import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Paper,
  Chip,
  Stack,
} from '@mui/material';
import {
  AccountCircle as UserIcon,
  AttachFile as AttachmentIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import { PurchaseRequest, RequestStatus } from '../../types/request.types';
import { requestService } from '../../services/requestService';
import { useAppSelector } from '../../store';

interface RequestDetailsProps {
  request: PurchaseRequest;
  onUpdate: () => void;
}

const RequestDetails: React.FC<RequestDetailsProps> = ({ request, onUpdate }) => {
  const [newComment, setNewComment] = useState('');
  const user = useAppSelector(state => state.auth.user);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      await requestService.addComment(request.id, newComment);
      setNewComment('');
      onUpdate();
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: request.currency,
    }).format(amount);
  };

  const getStatusColor = (status: RequestStatus) => {
    switch (status) {
      case RequestStatus.DRAFT:
        return 'default';
      case RequestStatus.PENDING_MANAGER:
      case RequestStatus.PENDING_SENIOR_MANAGER:
      case RequestStatus.PENDING_GENERAL_MANAGER:
      case RequestStatus.PENDING_FINANCE:
      case RequestStatus.PENDING_CFO:
        return 'warning';
      case RequestStatus.MORE_INFO_NEEDED:
        return 'info';
      case RequestStatus.APPROVED:
        return 'success';
      case RequestStatus.REJECTED:
        return 'error';
      case RequestStatus.PAID:
      case RequestStatus.CLOSED:
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {request.title}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Request ID
                  </Typography>
                  <Typography 
                    sx={{ 
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      color: 'text.primary',
                      backgroundColor: 'grey.100',
                      padding: '4px 8px',
                      borderRadius: 1,
                      display: 'inline-block',
                      userSelect: 'all'
                    }}
                  >
                    {request.id}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={request.status.replace(/_/g, ' ')}
                    color={getStatusColor(request.status)}
                    sx={{ mt: 0.5 }}
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Requester
                  </Typography>
                  <Typography>
                    {request.requester.firstName} {request.requester.lastName}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Department
                  </Typography>
                  <Typography>{request.department.name}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Category
                  </Typography>
                  <Typography>{request.category.replace(/_/g, ' ')}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography>{formatCurrency(request.amount)}</Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Urgency
                  </Typography>
                  <Typography>{request.urgency}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Frequency
                  </Typography>
                  <Typography>{request.frequency.charAt(0) + request.frequency.slice(1).toLowerCase()}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Requested Delivery Date
                  </Typography>
                  <Typography>
                    {format(new Date(request.requestedDeliveryDate), 'PPP')}
                  </Typography>
                </Box>

                {request.budgetCode && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Budget Code
                    </Typography>
                    <Typography>{request.budgetCode}</Typography>
                  </Box>
                )}

                {request.vendorName && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Vendor
                    </Typography>
                    <Typography>
                      {request.vendorName}
                      {request.vendorLink && (
                        <Button
                          href={request.vendorLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          sx={{ ml: 1 }}
                        >
                          Visit
                        </Button>
                      )}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Description
              </Typography>
              <Typography paragraph>{request.description}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Business Justification
              </Typography>
              <Typography paragraph>{request.justification}</Typography>
            </Grid>

            {request.attachments.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Attachments
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <List dense>
                    {request.attachments.map((attachment) => (
                      <ListItem
                        key={attachment.id}
                        button
                        component="a"
                        href={attachment.fileUrl}
                        target="_blank"
                      >
                        <ListItemAvatar>
                          <Avatar>
                            <AttachmentIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={attachment.filename}
                          secondary={format(new Date(attachment.createdAt), 'PPp')}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Comments
          </Typography>

          <List>
            {request.comments.map((comment) => (
              <React.Fragment key={comment.id}>
                <ListItem alignItems="flex-start">
                  <ListItemAvatar>
                    <Avatar>
                      <UserIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography component="span" variant="subtitle2">
                        {comment.user.firstName} {comment.user.lastName}
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          {format(new Date(comment.createdAt), 'PPp')}
                        </Typography>
                      </Typography>
                    }
                    secondary={comment.content}
                  />
                </ListItem>
                <Divider variant="inset" component="li" />
              </React.Fragment>
            ))}
          </List>

          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
            <Button
              variant="contained"
              onClick={handleAddComment}
              disabled={!newComment.trim()}
              sx={{ minWidth: 100 }}
            >
              <SendIcon />
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RequestDetails; 